import React from 'react';
import {
    Membership,
    MembershipBillingFrequency,
    MembershipEnrollmentType,
    MembershipRolloverUsage
} from '@spike/membership-model';
import { SelectableCard } from 'components/UI/V2/Forms/SelectableCard/SelectableCard';
import Button from 'components/UI/V2/Button/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxTaped, faRotate } from '@fortawesome/pro-regular-svg-icons';
import { createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import { Select } from 'components/UI/V2/Forms/Select/Select';
import { useMasterData } from 'hooks';
import { Label } from 'components/UI/V2/Forms/Label/Label';
import { Radio } from 'components/UI/V2/Forms/Radio/Radio';
import { CounterField } from 'components/UI/V2/CounterField/CounterField';
import clsx from 'clsx';

interface MembershipTypeStepProps {
    membership: Membership;
    onNext: (
        type: MembershipEnrollmentType,
        billingFrequency: MembershipBillingFrequency | '',
        rolloverUsage: MembershipRolloverUsage,
        rolloverBillingCycles: Membership['rolloverBillingCycles']
    ) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        membershipTypeColumn: {
            'gap': 8,
            'display': 'flex',
            'alignItems': 'center',
            'flexDirection': 'column',

            [theme.breakpoints.up('md')]: {
                gap: 16,
                flexDirection: 'row'
            },

            '& > p': {
                fontSize: 18,
                fontWeight: 500
            }
        },
        subscriptionFrequencyContainer: {
            gap: 24,
            display: 'flex',
            flexDirection: 'column',

            [theme.breakpoints.up('md')]: {
                width: '47%'
            }
        },
        radioOption: {
            gap: 16,
            display: 'flex',
            flexDirection: 'column'
        },
        radioGroupContainer: {
            gap: 20,
            display: 'flex',
            flexDirection: 'column'
        },
        inlineRadioGroupContainer: {
            gap: 16,
            display: 'flex',
            paddingLeft: 24,
            flexDirection: 'column',

            [theme.breakpoints.up('md')]: {
                gap: 24,
                flexDirection: 'row'
            }
        },
        counterFieldContainer: {
            gap: 8,

            [theme.breakpoints.down('sm')]: {
                flexDirection: 'row'
            }
        },
        counterField: {
            gap: 4
        },
        submitButton: {
            marginTop: 8,

            [theme.breakpoints.up('md')]: {
                alignSelf: 'flex-start'
            }
        }
    })
);

export const MembershipTypeStep: React.FC<MembershipTypeStepProps> = props => {
    const classes = useStyles();
    const masterdata = useMasterData();

    const [membershipType, setMembershipType] = React.useState(props.membership.enrollmentType);
    const [billingFrequency, setBillingFrequency] = React.useState(props.membership.billingFrequency);
    const [rolloverUsage, setRolloverUsage] = React.useState(props.membership.rolloverUsage);
    const [rolloverBillingCycles, setRolloverBillingCycles] = React.useState(props.membership.rolloverBillingCycles);

    const isValid = React.useMemo(() => {
        if (!membershipType) {
            return false;
        }

        if (membershipType === MembershipEnrollmentType.PACKAGE) {
            return true;
        }

        if (membershipType === MembershipEnrollmentType.SUBSCRIPTION && !billingFrequency) {
            return false;
        }

        if (membershipType === MembershipEnrollmentType.SUBSCRIPTION && !rolloverUsage) {
            return false;
        }

        if (
            membershipType === MembershipEnrollmentType.SUBSCRIPTION &&
            billingFrequency &&
            rolloverUsage === MembershipRolloverUsage.LIMITED &&
            !rolloverBillingCycles
        ) {
            return false;
        }

        return true;
    }, [membershipType, billingFrequency, rolloverUsage, rolloverBillingCycles]);

    const nextHandler = () => {
        if (!membershipType) {
            return;
        }

        props.onNext(
            membershipType as MembershipEnrollmentType,
            billingFrequency as MembershipBillingFrequency,
            rolloverUsage as MembershipRolloverUsage,
            rolloverBillingCycles as Membership['rolloverBillingCycles']
        );
    };

    React.useEffect(() => {
        // Reset billing frequency and rollover usage if package enrollment type is selected
        if (membershipType === MembershipEnrollmentType.PACKAGE) {
            setBillingFrequency('' as MembershipBillingFrequency);
            setRolloverUsage('' as MembershipRolloverUsage);
            setRolloverBillingCycles(null);
        }
    }, [membershipType]);

    React.useEffect(() => {
        // Set rollover billing cycles to 1 if rollover usage is limited
        if (rolloverUsage === MembershipRolloverUsage.LIMITED && !rolloverBillingCycles) {
            setRolloverBillingCycles(1);
        }

        // Reset rollover billing cycles if rollover usage is not limited
        if (rolloverUsage !== MembershipRolloverUsage.LIMITED) {
            setRolloverBillingCycles(null);
        }
    }, [rolloverUsage]);

    return (
        <>
            <div className={classes.membershipTypeColumn}>
                <SelectableCard
                    label="Package"
                    description="Set up packages with a single upfront payment."
                    icon={<FontAwesomeIcon icon={faBoxTaped} />}
                    checked={membershipType === MembershipEnrollmentType.PACKAGE}
                    onChange={() => setMembershipType(MembershipEnrollmentType.PACKAGE)}
                />

                <Typography>or</Typography>

                <SelectableCard
                    label="Subscription"
                    description="Setup recurring memberships with automatic payments."
                    icon={<FontAwesomeIcon icon={faRotate} />}
                    checked={membershipType === MembershipEnrollmentType.SUBSCRIPTION}
                    onChange={() => setMembershipType(MembershipEnrollmentType.SUBSCRIPTION)}
                />
            </div>

            {/* Show frequency options if subscription it's selected */}
            {membershipType === MembershipEnrollmentType.SUBSCRIPTION && (
                <>
                    <div className={classes.subscriptionFrequencyContainer}>
                        <Select
                            value={billingFrequency}
                            label="Billing Frequency"
                            placeholder="Select Billing Frequency"
                            options={masterdata.enrollmentBillingFrequencies.map(frequency => ({
                                label: frequency.name,
                                value: frequency.id
                            }))}
                            onChange={event => {
                                setBillingFrequency(event.target.value as MembershipBillingFrequency);
                                //****************************************************
                                //Remove this when the rollover usage is implemented
                                setRolloverUsage(MembershipRolloverUsage.RESET);
                                //****************************************************
                                //****************************************************
                            }}
                        />
                    </div>

                    {/* Show rollover options if billing frequency is selected */}
                    {billingFrequency && (
                        <>
                            <Label>Customize Your Subscription Usage</Label>

                            <div className={classes.radioGroupContainer}>
                                {/*
                                    //***************** ***********************************
                                    //Do not delete. This will be used in the v2
                                    //****************************************************
                                    <div className={classes.radioOption}>
                                        
                                        <Radio
                                            inlineLabel="Rollover"
                                            description="Unused items rollover to the next cycle"
                                            checked={[
                                                MembershipRolloverUsage.INDEFINITELY,
                                                MembershipRolloverUsage.LIMITED
                                            ].includes(rolloverUsage as MembershipRolloverUsage)}
                                            onChange={checked => {
                                                if (checked) {
                                                    setRolloverUsage(MembershipRolloverUsage.INDEFINITELY);
                                                }
                                            }}
                                        />
                                        

                                        {rolloverUsage && rolloverUsage !== MembershipRolloverUsage.RESET && (
                                            <div className={classes.inlineRadioGroupContainer}>
                                                <Radio
                                                    inlineLabel="Indefinitely"
                                                    checked={rolloverUsage === MembershipRolloverUsage.INDEFINITELY}
                                                    onChange={checked => {
                                                        if (checked) {
                                                            setRolloverUsage(MembershipRolloverUsage.INDEFINITELY);
                                                        }
                                                    }}
                                                />

                                                <div className={classes.radioOption}>
                                                    <Radio
                                                        inlineLabel="For a Set Number of Cycles"
                                                        checked={rolloverUsage === MembershipRolloverUsage.LIMITED}
                                                        onChange={checked => {
                                                            if (checked) {
                                                                setRolloverUsage(MembershipRolloverUsage.LIMITED);
                                                            }
                                                        }}
                                                    />

                                                    {rolloverUsage === MembershipRolloverUsage.LIMITED && (
                                                        <div
                                                            className={clsx(
                                                                classes.inlineRadioGroupContainer,
                                                                classes.counterFieldContainer
                                                            )}
                                                        >
                                                            <CounterField
                                                                minLimit={1}
                                                                value={Number(rolloverBillingCycles)}
                                                                className={classes.counterField}
                                                                onChange={value => setRolloverBillingCycles(value)}
                                                            />

                                                            <span>billing cycles</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    
                                    </div>
                                */}

                                <Radio
                                    inlineLabel="Reset"
                                    description="Resets with your billing cycle"
                                    checked={rolloverUsage === MembershipRolloverUsage.RESET}
                                    onChange={checked => {
                                        if (checked) {
                                            setRolloverUsage(MembershipRolloverUsage.RESET);
                                        }
                                    }}
                                />
                            </div>
                        </>
                    )}
                </>
            )}

            <Button
                label="Next"
                size="large"
                noColorOnDisabled
                disabled={!isValid}
                className={classes.submitButton}
                onClick={nextHandler}
            />
        </>
    );
};
